import { getTrackingAttributes } from '@rio-cloud/rio-uikit/googleAnalyticsUtils';
import { useIntl } from 'react-intl';

import { useShortLocale } from '#configuration/lang/useLocale.ts';

const VIDEO_EMBED_URL = 'https://www.youtube-nocookie.com/embed/9aXO6o5Wed8?start=195&rel=0';
const STEP_GRID_STYLE = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    listStyle: 'none',
    paddingLeft: 0,
};
const STEP_HEADER_STYLE = { minHeight: 40 };
const STEP_NUMBER_STYLE = { minWidth: 30 };
const VIDEO_CONTAINER_STYLE = { aspectRatio: '16 / 9' };

const getOrderCommunicationManualLink = (locale: string) =>
    `https://rio.cloud/${locale}/documents/order-communication/how-to/user-manual`;

const getTruckLoginManualLink = (locale: string) =>
    `https://rio.cloud/${locale}/documents/manuals/creation-of-a-pocket-driver-device-login/existing-vehicle`;

export const TourEmptyState = () => {
    const intl = useIntl();
    const locale = useShortLocale();
    const orderCommunicationManualLink = getOrderCommunicationManualLink(locale);
    const helpBlocks = [
        {
            title: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-tour.title',
                defaultMessage: 'How do I create a tour?',
            }),
            links: [
                {
                    href: `${orderCommunicationManualLink}/tours`,
                    trackingKey: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-tour.tours-manual',
                    label: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-tour.tours-manual',
                        defaultMessage: 'Open the Tours manual',
                    }),
                },
                {
                    href: `${orderCommunicationManualLink}/orders#c152083`,
                    trackingKey: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-tour.orders-section',
                    label: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-tour.orders-section',
                        defaultMessage: 'Open the Orders section for planning tours',
                    }),
                },
            ],
        },
        {
            title: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.connect-vehicle.title',
                defaultMessage: 'How do I link orders and vehicle?',
            }),
            links: [
                {
                    href: getTruckLoginManualLink(locale),
                    trackingKey: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.connect-vehicle.truck-login',
                    label: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.connect-vehicle.truck-login',
                        defaultMessage: 'Open Truck Login / vehicle setup',
                    }),
                },
            ],
        },
        {
            title: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-order.title',
                defaultMessage: 'How do I create an order?',
            }),
            links: [
                {
                    href: `${orderCommunicationManualLink}/orders`,
                    trackingKey: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-order.orders-manual',
                    label: intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.create-order.orders-manual',
                        defaultMessage: 'Open the Orders manual',
                    }),
                },
            ],
        },
    ];

    return (
        <div data-testid='tour-empty-state' className='display-flex justify-content-center max-width-100pct'>
            <div className='margin-0 width-100pct max-width-1000 panel-default panel padding-15 border-none'>
                <div className='display-flex flex-column align-items-center text-center padding-25'>
                    <div className='text-size-h3 text-size-h2-xl line-height-125rel text-color-dark text-medium width-500 max-width-100pct text-wrap-balance'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.title',
                            defaultMessage: 'No tours available',
                        })}
                    </div>
                    <div className='text-color-dark margin-top-15 width-500 max-width-100pct text-wrap-balance'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.message',
                            defaultMessage:
                                'Tours are created from orders and assigned vehicles. These links explain the required setup and the first planning steps.',
                        })}
                    </div>

                    <ol className='display-grid gap-20 width-100pct margin-top-25 text-left' style={STEP_GRID_STYLE}>
                        {helpBlocks.map((helpBlock, index) => (
                            <li key={helpBlock.title} className='bg-lightest rounded padding-15'>
                                <div
                                    className='display-flex align-items-center gap-10 margin-bottom-15'
                                    style={STEP_HEADER_STYLE}
                                >
                                    <div
                                        className='display-flex align-items-center justify-content-center rounded-circle bg-primary text-color-white text-medium width-30 height-30 flex-shrink-0'
                                        data-testid='tour-empty-state-step-number'
                                        style={STEP_NUMBER_STYLE}
                                        aria-hidden='true'
                                    >
                                        {index + 1}
                                    </div>
                                    <div className='text-medium text-color-dark'>{helpBlock.title}</div>
                                </div>
                                <div className='display-flex flex-column gap-10'>
                                    {helpBlock.links.map(link => (
                                        <a
                                            key={link.href}
                                            href={link.href}
                                            target='_blank'
                                            rel='noreferrer'
                                            {...getTrackingAttributes({
                                                trigger: 'click',
                                                category: 'link autoTracking',
                                                action: 'link click',
                                                label: link.trackingKey,
                                            })}
                                        >
                                            <span
                                                className='rioglyph rioglyph-new-window margin-right-5'
                                                aria-hidden='true'
                                            />
                                            {link.label}
                                        </a>
                                    ))}
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className='width-100pct margin-top-25 text-left'>
                        <div className='text-medium text-color-dark margin-bottom-10'>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.video.title',
                                defaultMessage: 'Video tutorial',
                            })}
                        </div>
                        <div className='width-100pct bg-lightest rounded overflow-hidden' style={VIDEO_CONTAINER_STYLE}>
                            <iframe
                                data-testid='tour-empty-state-video'
                                className='width-100pct height-100pct border-none'
                                src={VIDEO_EMBED_URL}
                                title={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.empty-state.help.video.iframe-title',
                                    defaultMessage: 'Step-by-step tour planning tutorial',
                                })}
                                loading='lazy'
                                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                                referrerPolicy='strict-origin-when-cross-origin'
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
